import React, { useMemo } from 'react'
import moment from 'moment'
import { useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useNotificationContext } from '../../../../../context/notification/context'
import { useApiFormOrganization } from '../../../../../api-new/state/content'
import { Card } from '../../../../../shared/card/card'
import { Form, FormSubmitBack } from '../../../../../shared/form/form'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { IconCalender } from '../../../../../shared/icon/icon'
import { serverMessage } from '../../../../../shared/error/server-message'
import { purgeDay } from './organization-recycle'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

// Support extends one organization's deletion window (delete-organization plan
// Task 24): a later purge date and a reason, sent to PUT /organization/:id/extend.
// The server also checks the date (in the future, after the current one, at most
// 365 days ahead; a held deletion whose hold was lifted needs 7 days) and records
// the change, reason and actor in the audit trail.

const Field = ({ id, label, children }) =>
  <div className="form-group my-2">
    <label htmlFor={id} className="mb-2">
      {label}
    </label>
    {children}
  </div>

const ExtendFields = () => {
  const { register } = useFormContext()
  return (
    <React.Fragment>
      <Field id="extend-purge-after" label="New purge date">
        <input id="extend-purge-after" type="date" className="form-control" {...register('purgeDate')} />
      </Field>
      <Field id="extend-reason" label="Reason">
        <textarea id="extend-reason" className="form-control" rows={3} placeholder="Why the organization needs more time" {...register('reason')} />
      </Field>
    </React.Fragment>
  )
}

// The chosen day at the current purge date's time of day (UTC), so moving a
// date by N days moves it by exactly N days.
export const extendedPurgeAfter = (day, current) => {
  const at = moment.utc(current)
  return moment
    .utc(day, 'YYYY-MM-DD', true)
    .hour(at.hour())
    .minute(at.minute())
    .second(at.second())
    .millisecond(0)
}

export const OrganizationExtend = () => {
  const { extendID } = useParamsInt()
  const history = useHistory()
  const apiForm = useApiFormOrganization()
  const notificationContext = useNotificationContext()

  const [error, failure, setError] = useErrorLine()

  const row = apiForm.form || {}
  const current = row.purgeAfter

  const state = useMemo(() => ({ purgeDate: '', reason: '' }), [])

  const handleMatch = () => apiForm.get(`${extendID}/deleted`)

  const handleSubmit = async values => {
    setError(null)

    const reason = (values.reason || '').trim()
    const next = values.purgeDate ? extendedPurgeAfter(values.purgeDate, current) : null

    if (!current) return
    if (!next || !next.isValid() || !next.isAfter(moment.utc(current))) {
      setError(`Choose a date after ${purgeDay(current)}.`)
      return
    }
    if (!reason) {
      setError('Give a reason. It is kept in the audit trail.')
      return
    }

    try {
      await apiForm.put(`${extendID}/extend`, { purgeAfter: next.toISOString(), reason })
      notificationContext.toast.notify(`Purge date moved to ${purgeDay(next)}`, 3000)
      history.push('/admin/organization/list/recycle')
    } catch (err) {
      setError(serverMessage(err, "This deletion couldn't be extended."), err)
    }
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconCalender} text="extend" onMatch={handleMatch}>
        <Card title={`extend ${row.name || ''}`}>
          {current &&
            <div className="py-2">
              Currently purged on {purgeDay(current)}. The owners and admins are emailed the new date.
            </div>}
          {error &&
            <p className="text-danger" role="alert">
              {error}
            </p>}
          {error && <ErrorReference error={failure} className="mb-3" />}
          <Form state={state} onSubmit={handleSubmit}>
            <ExtendFields />
            {/* No current date until the deletion has loaded (review M7). */}
            <FormSubmitBack text="extend" disable={!current} />
          </Form>
        </Card>
      </BreadcrumbView>
    </React.Fragment>
  )
}
